import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__';
import { EmployeeType } from '__generated__/graphql';
import { FC } from 'react';
import SearchDesignations from 'routes/Department/Users/CreateUser/Employee/SearchDesignations';

import Columns from 'components/Columns';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';
import LoadingIndicator from 'components/LoadingIndicator';

const DEPARTMENT_QUERY = gql(`
  query DepartmentDivisions($departmentId: ID!) {
    department(id: $departmentId) {
      id
      divisions {
        id
        name
      }
    }
  }
`);

const UPDATE_EMPLOYEE_MUTATION = gql(`
  mutation UpdateEmployee(
    $id: ID!
    $fileNumber: String!
    $organisationCode: String
    $signature: Upload
    $photo: Upload
    $workEmail: String
    $workPhoneNumber: String
    $divisionId: ID!
    $designationId: ID!
  ) {
    updateEmployee(
      id: $id
      fileNumber: $fileNumber
      organisationCode: $organisationCode
      signature: $signature
      photo: $photo
      workEmail: $workEmail
      workPhoneNumber: $workPhoneNumber
      divisionId: $divisionId
      designationId: $designationId
    ) {
      id
      fileNumber
      organisationCode
      signature
      photo
      workEmail
      workPhoneNumber
      division {
        id
        name
      }
      designation {
        id
        name
      }
    }
  }
`);

const UpdateEmployee: FC<{
  employee: EmployeeType;
  departmentId: string;
}> = ({ employee, departmentId }) => {
  const { data, loading, error } = useQuery(DEPARTMENT_QUERY, {
    variables: {
      departmentId,
    },
  });

  const [updateEmployee, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_EMPLOYEE_MUTATION);

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} />;

  return (
    <FormPanelWithReadMode
      loading={updateLoading}
      error={updateError}
      onSubmit={formData => {
        updateEmployee({
          variables: {
            id: employee.id,
            fileNumber: formData.fileNumber,
            organisationCode: formData.organisationCode,
            signature: formData.signature instanceof File ? formData.signature : undefined,
            photo: formData.photo instanceof File ? formData.photo : undefined,
            workEmail: formData.workEmail,
            workPhoneNumber: formData.workPhoneNumber,
            divisionId: formData.divisionId,
            designationId: formData.designation.id,
          },
        });
      }}
      title="Employee Information"
    >
      <FormInput
        fieldName="fileNumber"
        type="string"
        fullWidth
        defaultValue={employee.fileNumber || ''}
        label="File Number"
        validators={{
          required: true,
        }}
      />
      {departmentId === '20009c7b-5c40-4d60-b3cc-9ea4c801112e' ? (
        <FormInput
          fieldName="organisationCode"
          type="select"
          fullWidth
          defaultValue={employee.organisationCode}
          label="Organisation"
          options={[
            {
              label: 'Department Of Education',
              value: 'DOE',
            },
            {
              label: 'Teaching Service Commission',
              value: 'TSC',
            },
            {
              label: 'Office of Library and Archive',
              value: 'OLA',
            },
            {
              label: 'Ministry of Education',
              value: 'MOE',
            },
          ]}
          validators={{
            required: true,
          }}
        />
      ) : (
        <></>
      )}
      <Columns number={2} bordered>
        <FormInput
          fieldName="signature"
          type="upload"
          fullWidth
          defaultValue={employee.signature || ''}
          label="Signature"
          validators={{
            required: true,
          }}
        />
        <FormInput
          fieldName="photo"
          type="upload"
          fullWidth
          defaultValue={employee.photo || ''}
          label="Photo"
          validators={{
            required: true,
          }}
        />
      </Columns>
      <Columns number={2} bordered>
        <FormInput
          fieldName="workEmail"
          type="string"
          fullWidth
          defaultValue={employee.workEmail}
          label="Work Email"
        />
        <FormInput
          fieldName="workPhoneNumber"
          type="string"
          fullWidth
          defaultValue={employee.workPhoneNumber}
          label="Work Phone Number"
        />
      </Columns>
      <Columns number={2} bordered>
        <FormInput
          fieldName="divisionId"
          type="select"
          fullWidth
          defaultValue={employee.division.id}
          label="Division"
          options={data.department.divisions.map(division => ({
            label: division.name,
            value: division.id,
          }))}
          validators={{
            required: true,
          }}
        />
        <FormInput
          fullWidth
          type="custom"
          fieldName="designation"
          label="Designation"
          defaultValue={employee.designation || ''}
          customInput={SearchDesignations}
          validators={{
            required: true,
          }}
        />
      </Columns>
    </FormPanelWithReadMode>
  );
};

export default UpdateEmployee;
